import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useHotkeys } from "react-hotkeys-hook";
import { LuArrowDown, LuArrowUp, LuChevronsUpDown, LuEllipsis, LuKey, LuLock, LuMail, LuPlus, LuSearch, LuTrash2 } from "react-icons/lu";
import API from "../../api";
import { capture } from "../../instrument";
import { getSetting } from "../../queries/setting";
import { PASSWORD_MIN_LENGTH, USER_ROLE_LABEL, isPasswordStrong } from "../../utils";
import AddUserPanel from "./AddUserPanel";

const ROLE_HIERARCHY = ["user", "admin", "superadmin"];

const UsersSection = ({ user }) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [menuUserId, setMenuUserId] = useState(null);
  const [newPasswordUserId, setNewPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [groupDraft, setGroupDraft] = useState(null);
  useHotkeys("escape", () => setMenuUserId(null), { enabled: menuUserId !== null });

  const { data: users, isError } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { ok, data, message } = await API.get("/user/all");
      if (!ok) throw new Error(message);
      return data;
    },
  });
  const { data: setting } = useQuery({ queryKey: ["setting"], queryFn: getSetting });

  const resetPasswordMutation = useMutation({
    mutationFn: (targetUser) => API.post(`/user/reset-password/${targetUser._id}`),
    onSuccess: (response, targetUser) => {
      toast.success(targetUser.is_locked ? "Le compte a été débloqué et le lien de réinitialisation a été envoyé" : "Le lien de réinitialisation a été envoyé");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible d'envoyer le lien de réinitialisation");
    },
  });

  const passwordMutation = useMutation({
    mutationFn: ({ targetUser, password }) => API.put(`/user/${targetUser._id}`, { password }),
    onSuccess: (response, { targetUser }) => {
      toast.success(targetUser.is_locked ? "Le compte a été débloqué et le mot de passe défini" : "Le mot de passe a été défini");
      setNewPasswordUserId(null);
      setNewPassword("");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de définir le mot de passe");
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ targetUser, role }) => API.put(`/user/${targetUser._id}`, { role }),
    onMutate: ({ targetUser, role }) => {
      const previousUsers = queryClient.getQueryData(["users"]);
      queryClient.setQueryData(["users"], (current) => current.map((u) => (u._id === targetUser._id ? { ...u, role } : u)));
      return { previousUsers };
    },
    onSuccess: (response, { role }) => toast.success(`Rôle mis à jour : ${USER_ROLE_LABEL[role]}`),
    onError: (error, variables, { previousUsers }) => {
      queryClient.setQueryData(["users"], previousUsers);
      capture(error);
      toast.error(error.message || "Impossible de modifier le rôle");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => API.delete(`/user/${id}`),
    onSuccess: () => {
      toast.success("L'utilisateur a été supprimé");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de supprimer l'utilisateur");
    },
  });

  const groupMutation = useMutation({
    mutationFn: (group) => API.post("/setting", { group }),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(["setting"], data);
      setGroupDraft(null);
      toast.success("Groupe mis à jour");
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Erreur d'enregistrement du groupe");
    },
  });

  const onSendResetPassword = (targetUser) => {
    setMenuUserId(null);
    const confirmed = window.confirm(
      targetUser.is_locked
        ? "Voulez-vous vraiment débloquer ce compte et envoyer un lien de réinitialisation de mot de passe ?"
        : "Voulez-vous vraiment envoyer un lien de réinitialisation de mot de passe à cet utilisateur ?",
    );
    if (confirmed) resetPasswordMutation.mutate(targetUser);
  };

  const onEditPassword = (targetUser) => {
    setMenuUserId(null);
    setNewPasswordUserId(targetUser._id);
    setNewPassword("");
  };

  const onDeleteUser = (id) => {
    setMenuUserId(null);
    if (window.confirm("Voulez-vous vraiment supprimer cet utilisateur ? Cette action est irréversible.")) deleteMutation.mutate(id);
  };

  const group = groupDraft ?? setting?.group ?? "";
  const normalizedSearch = search.trim().toLowerCase();
  const filteredUsers = (users || []).filter((u) => !normalizedSearch || [u.username, u.email].some((value) => value?.toLowerCase().includes(normalizedSearch)));
  const sortedUsers = [...filteredUsers].sort((a, b) =>
    sortKey === "lastLogin" ? new Date(b.last_login_at || 0) - new Date(a.last_login_at || 0) : (a.username || a.email).localeCompare(b.username || b.email, "fr"),
  );

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Utilisateurs</h2>
        <p className="mt-1.5 text-sm text-gray-500">Comptes qui peuvent se connecter à cet espace.</p>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <label className="flex h-8 w-64 items-center gap-2 rounded-md border border-transparent bg-gray-200/60 px-2.5 text-gray-400 transition-colors focus-within:border-primary-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-500/20 hover:bg-gray-200">
          <LuSearch className="size-3.5 shrink-0" />
          <input
            type="search"
            name="userSearch"
            aria-label="Rechercher un utilisateur"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher"
            className="min-w-0 grow bg-transparent text-[13px] text-gray-900 outline-none placeholder:text-gray-400"
          />
        </label>
        <button
          type="button"
          onClick={() => setAddUserOpen(true)}
          className="flex h-8 items-center gap-1.5 rounded-md bg-primary-500 pl-2 pr-3 text-[13px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_2px_rgba(0,0,0,0.12)] transition-colors hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
        >
          <LuPlus className="size-4" />
          Ajouter un utilisateur
        </button>
      </div>

      {isError && <p className="text-sm text-gray-500">Impossible de charger les utilisateurs.</p>}

      {!users && !isError && (
        <div className="flex justify-center py-16">
          <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
        </div>
      )}

      {users && (
        <div className="rounded-xl bg-white px-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th scope="col" aria-sort={sortKey === "name" ? "ascending" : "none"} className="whitespace-nowrap py-2 pr-4 text-left text-xs font-medium text-gray-500">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setSortKey("name")} className="flex items-center gap-1 transition-colors hover:text-gray-900">
                      Utilisateur
                      {sortKey === "name" && <LuArrowUp className="size-3" />}
                    </button>
                    <span className="font-normal tabular-nums text-gray-400">{filteredUsers.length}</span>
                  </div>
                </th>
                <th scope="col" className="whitespace-nowrap py-2 pr-4 text-left text-xs font-medium text-gray-500">
                  Rôle
                </th>
                <th
                  scope="col"
                  aria-sort={sortKey === "lastLogin" ? "descending" : "none"}
                  className="whitespace-nowrap py-2 pr-4 text-left text-xs font-medium text-gray-500 max-md:hidden"
                >
                  <button type="button" onClick={() => setSortKey("lastLogin")} className="flex items-center gap-1 transition-colors hover:text-gray-900">
                    Dernière connexion
                    {sortKey === "lastLogin" && <LuArrowDown className="size-3" />}
                  </button>
                </th>
                <th scope="col" className="py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-12 text-center text-sm text-gray-500">
                    {normalizedSearch ? "Aucun utilisateur ne correspond à la recherche" : "Aucun utilisateur"}
                  </td>
                </tr>
              )}
              {sortedUsers.map((u) => (
                <tr key={u._id}>
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="grid size-7 shrink-0 place-items-center rounded-full bg-gray-100 text-[11px] font-medium uppercase text-gray-600">
                        {(u.username || u.email).slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-gray-900">
                          {u.username || u.email}
                          {u._id === user._id && <span className="chip border-gray-200 bg-gray-50 font-normal text-gray-600">vous</span>}
                          {u.username && <span className="chip border-gray-200 text-gray-600">Identifiant</span>}
                          {u.is_locked && (
                            <span className="chip border-red-200 bg-red-50 text-red-700">
                              <LuLock className="size-3" />
                              Bloqué
                            </span>
                          )}
                          {!u.last_login_at && u.email && <span className="chip border-gray-200 bg-gray-50 text-gray-600">Invitation envoyée</span>}
                          {u.mustChangePassword && <span className="chip border-gray-200 bg-gray-50 text-gray-600">Mot de passe provisoire</span>}
                        </div>
                        {u.username && u.email && <p className="text-xs text-gray-500">{u.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-4 text-[13px] text-gray-600">
                    {u._id !== user._id && ROLE_HIERARCHY.indexOf(u.role) <= ROLE_HIERARCHY.indexOf(user.role) ? (
                      <div className="relative -ml-2 inline-flex items-center">
                        <select
                          name={`role-${u._id}`}
                          aria-label={`Rôle de ${u.username || u.email}`}
                          value={u.role}
                          disabled={roleMutation.isPending && roleMutation.variables.targetUser._id === u._id}
                          onChange={(e) => roleMutation.mutate({ targetUser: u, role: e.target.value })}
                          className="h-7 cursor-pointer appearance-none rounded-md bg-transparent pl-2 pr-7 text-[13px] text-gray-600 outline-none transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-wait disabled:opacity-50"
                        >
                          {ROLE_HIERARCHY.slice(0, ROLE_HIERARCHY.indexOf(user.role) + 1).map((role) => (
                            <option key={role} value={role}>
                              {USER_ROLE_LABEL[role]}
                            </option>
                          ))}
                        </select>
                        <LuChevronsUpDown className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
                      </div>
                    ) : (
                      USER_ROLE_LABEL[u.role]
                    )}
                  </td>
                  <td className={`whitespace-nowrap py-2.5 pr-4 text-[13px] tabular-nums max-md:hidden ${u.last_login_at ? "text-gray-600" : "text-gray-400"}`}>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "Jamais"}
                  </td>
                  <td className="py-2.5">
                    {u._id !== user._id && newPasswordUserId === u._id && (
                      <form
                        className="flex items-center justify-end gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          passwordMutation.mutate({ targetUser: u, password: newPassword });
                        }}
                      >
                        <input
                          type="password"
                          name="newPassword"
                          autoFocus
                          autoComplete="new-password"
                          aria-label="Nouveau mot de passe"
                          className="h-8 w-48 rounded-md border border-transparent bg-gray-100 px-2.5 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 hover:bg-gray-200/70 focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20"
                          placeholder="Nouveau mot de passe"
                          title={`${PASSWORD_MIN_LENGTH} caractères minimum et trois types de caractères parmi minuscules, majuscules, chiffres et spéciaux`}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button
                          type="submit"
                          disabled={!isPasswordStrong(newPassword) || passwordMutation.isPending}
                          className="h-8 rounded-md bg-white px-3 text-[13px] font-medium text-gray-700 shadow-sm ring-1 ring-gray-950/10 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Valider
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewPasswordUserId(null)}
                          className="h-8 rounded-md px-2 text-[13px] text-gray-500 transition-colors hover:bg-gray-100"
                        >
                          Annuler
                        </button>
                      </form>
                    )}
                    {u._id !== user._id && newPasswordUserId !== u._id && (
                      <div className="relative flex justify-end">
                        <button
                          type="button"
                          aria-label={`Actions pour ${u.username || u.email}`}
                          aria-haspopup="menu"
                          aria-expanded={menuUserId === u._id}
                          onClick={() => setMenuUserId(menuUserId === u._id ? null : u._id)}
                          className={`grid size-7 place-items-center rounded-md transition-colors hover:bg-gray-100 hover:text-gray-700 ${
                            menuUserId === u._id ? "bg-gray-100 text-gray-700" : "text-gray-400"
                          }`}
                        >
                          <LuEllipsis className="size-4" />
                        </button>
                        {menuUserId === u._id && (
                          <>
                            <button type="button" aria-hidden="true" tabIndex={-1} className="fixed inset-0 z-10 cursor-default" onClick={() => setMenuUserId(null)} />
                            <div role="menu" className="absolute right-0 top-8 z-20 w-72 rounded-lg bg-white/75 p-1 shadow-lg ring-1 ring-gray-950/10 backdrop-blur-xl">
                              {u.email ? (
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => onSendResetPassword(u)}
                                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-100"
                                >
                                  <LuMail className="size-4 shrink-0 text-gray-400" />
                                  {u.is_locked ? "Débloquer et envoyer un lien de réinitialisation" : "Envoyer un lien de réinitialisation"}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => onEditPassword(u)}
                                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-100"
                                >
                                  <LuKey className="size-4 shrink-0 text-gray-400" />
                                  {u.is_locked ? "Débloquer et définir un mot de passe" : "Définir un nouveau mot de passe"}
                                </button>
                              )}
                              {ROLE_HIERARCHY.indexOf(u.role) <= ROLE_HIERARCHY.indexOf(user.role) && (
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => onDeleteUser(u._id)}
                                  className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] text-red-600 transition-colors hover:bg-red-50"
                                >
                                  <LuTrash2 className="size-4 shrink-0" />
                                  Supprimer l'utilisateur
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {user.role === "superadmin" && setting && (
        <>
          <div className="mb-3 mt-10 flex items-center gap-2">
            <h3 className="text-[15px] font-medium text-gray-900">Identifiants de connexion</h3>
            <span className="chip border-gray-200 bg-gray-50 text-gray-600">Super admin</span>
          </div>
          <div className="overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
            <form
              className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3.5"
              onSubmit={(e) => {
                e.preventDefault();
                groupMutation.mutate(group);
              }}
            >
              <div className="min-w-0 flex-1 basis-60">
                <label htmlFor="username-group" className="text-[13px] font-medium text-gray-900">
                  Suffixe des identifiants
                </label>
                <p className="mt-0.5 text-[13px] text-gray-500">
                  Les comptes créés ensuite prennent la forme identifiant@{group.trim().toLowerCase() || user.indexName}. Vide : nom du client.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="flex h-8 w-64 items-center rounded-md border border-transparent bg-gray-100 transition-colors focus-within:border-primary-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-500/20 hover:bg-gray-200/70">
                  <p className="pl-2.5 text-[13px] text-gray-400">@</p>
                  <input
                    id="username-group"
                    name="group"
                    type="text"
                    placeholder={user.indexName}
                    value={group}
                    onChange={(e) => setGroupDraft(e.target.value)}
                    className="min-w-0 grow rounded-r-md bg-transparent px-1 text-[13px] text-gray-900 outline-none placeholder:text-gray-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={groupMutation.isPending || group === (setting.group ?? "")}
                  className="h-8 rounded-md bg-white px-3 text-[13px] font-medium text-gray-700 shadow-sm ring-1 ring-gray-950/10 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {addUserOpen && (
        <AddUserPanel
          usernameSuffix={setting?.group || user.indexName}
          onClose={() => setAddUserOpen(false)}
          onCreated={() => {
            setAddUserOpen(false);
            queryClient.invalidateQueries({ queryKey: ["users"] });
          }}
        />
      )}
    </div>
  );
};

export default UsersSection;
